import { getRoomName } from '../base/conference/functions';
import { MEDIA_TYPE } from '../base/media/constants';
import { getLocalParticipant, getParticipantDisplayName } from '../base/participants/functions';
import { getVideoTrackByParticipant } from '../base/tracks/functions';
import { isInterpreter } from '../base/user-role/functions';
import { isVriApp } from '../base/whitelabel/functions';
import { isObserverRoomPage } from '../vrs-auth/observerRoomPage';
import { getPersistentItem, getPersistentJson } from '../vrs-auth/storage';
import { VISUAL_LANGUAGE_GROUP_TILE_CAP_PARTICIPANTS, VISUAL_LANGUAGE_INTERPRETER_FOCUS_PARTICIPANT_LIMIT, VISUAL_LANGUAGE_SMALL_ROOM_PARTICIPANT_LIMIT } from './constants';
import { expectedMediaTransport } from './p2pPolicy';
import { countConferencePeople, getActiveTransport } from './p2pTransport';
import { inferPlatformRoomRoles, inferRemoteRoles, normalizeVisualRole as normalizeRole } from './roles';
export { inferRemoteRoles };
/**
 * The page's `?role=`. Parsed by hand: React Native has no `window.location`,
 * and its URLSearchParams throws on get(), so native code reaching this (a
 * signed-in user with no stored role) must get undefined, not an exception.
 *
 * @returns {VisualLanguageParticipantRole|undefined}
 */
function getWindowRole() {
    const search = typeof window === 'undefined' ? undefined : window.location?.search;
    if (typeof search !== 'string' || !search) {
        return undefined;
    }
    const match = /[?&]role=([^&#]*)/.exec(search);
    if (!match) {
        return undefined;
    }
    try {
        return normalizeRole(decodeURIComponent(match[1].replace(/\+/g, ' ')));
    }
    catch {
        return undefined;
    }
}
function getStoredVisualRole() {
    return normalizeRole(getPersistentItem('vrs_user_role'))
        || getWindowRole()
        || normalizeRole(getPersistentJson('vrs_user_info')?.role);
}
function getStoredTargetClient() {
    return getPersistentItem('vrs_target_client') || undefined;
}
function isVriHostname() {
    if (typeof window === 'undefined') {
        return false;
    }
    const hostname = window.location.hostname.toLowerCase();
    return hostname.startsWith('vri.')
        || hostname.startsWith('staging-vri.')
        || hostname.includes('maplecomm.ca');
}
function getRoomKind(state) {
    const roomName = getRoomName(state);
    if (roomName?.startsWith('vrs-')) {
        return 'vrs';
    }
    // Deaf-to-deaf rooms: everyone signs, nobody is "the interpreter".
    if (roomName?.startsWith('p2p-') || roomName?.startsWith('group-')) {
        return 'p2p';
    }
    if (roomName?.startsWith('vri-') || roomName?.startsWith('instant-')) {
        return 'vri';
    }
    if (isVriApp() || isVriHostname()) {
        return 'vri';
    }
    if (getStoredVisualRole() || getStoredTargetClient()) {
        return 'vrs';
    }
    return 'generic';
}
function isVisibleParticipant(participant) {
    return Boolean(participant)
        && !participant?.fakeParticipant
        && !participant?.isJigasi
        && !participant?.botType;
}
function getVisibleParticipants(state) {
    const localParticipant = getLocalParticipant(state);
    const remoteParticipants = Array.from(state['features/base/participants'].remote.values())
        .filter(isVisibleParticipant);
    return [
        ...(isVisibleParticipant(localParticipant) && localParticipant ? [localParticipant] : []),
        ...remoteParticipants
    ];
}
export function getLocalVisualLanguageRole() {
    // The browser's saved account role is not the observer's.
    if (isObserverRoomPage()) {
        return 'admin-observer';
    }
    const storedRole = getStoredVisualRole();
    if (storedRole) {
        return storedRole;
    }
    if (isVriApp()) {
        return isInterpreter() ? 'interpreter' : 'client';
    }
    return 'guest';
}
export function getVisualLanguageMediaPolicy(state) {
    const roomKind = getRoomKind(state);
    if (roomKind === 'generic') {
        return {
            enabled: false,
            guestParticipantIds: [],
            lastN: 0,
            mode: 'not-interpreted',
            participantCount: 0,
            priorityParticipantIds: [],
            roleByParticipantId: {},
            roomKind
        };
    }
    if (roomKind === 'p2p') {
        return getPlatformRoomMediaPolicy(state);
    }
    const localParticipant = getLocalParticipant(state);
    const remoteParticipants = Array.from(state['features/base/participants'].remote.values())
        .filter(isVisibleParticipant);
    const localRole = getLocalVisualLanguageRole();
    const roleByParticipantId = inferRemoteRoles(remoteParticipants, localRole, getStoredTargetClient());
    if (localParticipant?.id && isVisibleParticipant(localParticipant)) {
        roleByParticipantId[localParticipant.id] = localRole;
    }
    const participants = getVisibleParticipants(state);
    const participantCount = participants.length;
    const priorityRoles = ['client', 'interpreter'];
    const { participantId: largeVideoParticipantId } = state['features/large-video'];
    const priorityParticipantIds = participants
        .filter(participant => {
        const role = roleByParticipantId[participant.id] || 'guest';
        return priorityRoles.includes(role)
            || participant.pinned
            || participant.id === largeVideoParticipantId;
    })
        .map(participant => participant.id);
    const guestParticipantIds = participants
        .filter(participant => !priorityParticipantIds.includes(participant.id))
        .map(participant => participant.id);
    const mode = participantCount <= VISUAL_LANGUAGE_SMALL_ROOM_PARTICIPANT_LIMIT
        ? 'small-room-signing'
        : 'interpreter-focused';
    const lastN = participantCount <= VISUAL_LANGUAGE_SMALL_ROOM_PARTICIPANT_LIMIT
        ? VISUAL_LANGUAGE_SMALL_ROOM_PARTICIPANT_LIMIT - 1
        : VISUAL_LANGUAGE_INTERPRETER_FOCUS_PARTICIPANT_LIMIT - 1;
    return {
        enabled: true,
        guestParticipantIds,
        lastN,
        mode,
        participantCount,
        priorityParticipantIds,
        roleByParticipantId,
        roomKind
    };
}
/**
 * A deaf-to-deaf call: every participant signs, so no one is guessed to be
 * the interpreter (the positional guess used for interpreted rooms would
 * give one random signer 720p and cap the rest). Everyone is received
 * (lastN -1; calls hold 8, shared/call-capacity.js). Up to four people, all
 * tiles at the ASL floor; from five, tiles are capped at 360p/30fps and the
 * active or pinned signer (and an interpreter, if one was called in) keeps
 * the ASL floor.
 *
 * @param {IReduxState} state - The redux state.
 * @returns {IVisualLanguageMediaPolicy}
 */
function getPlatformRoomMediaPolicy(state) {
    const localParticipant = getLocalParticipant(state);
    const participants = getVisibleParticipants(state);
    const participantCount = participants.length;
    const { participantId: largeVideoParticipantId } = state['features/large-video'];
    const roleByParticipantId = inferPlatformRoomRoles(participants.filter(participant => !participant.local));
    if (localParticipant?.id && participants.some(participant => participant.id === localParticipant.id)) {
        roleByParticipantId[localParticipant.id] = getLocalVisualLanguageRole();
    }
    const remote = participants.filter(participant => participant.id !== localParticipant?.id);
    if (participantCount < VISUAL_LANGUAGE_GROUP_TILE_CAP_PARTICIPANTS) {
        return {
            enabled: true,
            guestParticipantIds: [],
            lastN: -1,
            mode: 'small-room-signing',
            participantCount,
            priorityParticipantIds: remote.map(participant => participant.id),
            roleByParticipantId,
            roomKind: 'p2p'
        };
    }
    const isFocused = (participant) => participant.pinned
        || participant.id === largeVideoParticipantId
        || roleByParticipantId[participant.id] === 'interpreter';
    return {
        enabled: true,
        guestParticipantIds: remote.filter(participant => !isFocused(participant)).map(participant => participant.id),
        lastN: -1,
        mode: 'group-signing',
        participantCount,
        priorityParticipantIds: remote.filter(isFocused).map(participant => participant.id),
        roleByParticipantId,
        roomKind: 'p2p'
    };
}
/**
 * Remote participants the receiver constraints must cover: the priority
 * signers, and in a group call every other tile too (capped, not dropped).
 *
 * @param {IVisualLanguageMediaPolicy} policy - The current policy.
 * @returns {string[]}
 */
export function getVisualLanguageReceiveParticipantIds(policy) {
    return policy.mode === 'group-signing'
        ? [...policy.priorityParticipantIds, ...policy.guestParticipantIds]
        : policy.priorityParticipantIds;
}
export function getVisualLanguageMediaEvidence(state) {
    const policy = getVisualLanguageMediaPolicy(state);
    if (!policy.enabled) {
        return undefined;
    }
    const participants = getVisibleParticipants(state);
    const videoTracks = state['features/base/tracks'].filter(track => track.mediaType === MEDIA_TYPE.VIDEO);
    const remoteVideoTracks = videoTracks.filter(track => !track.local);
    const visibleRemoteVideoTracks = remoteVideoTracks
        .filter(track => track.muted !== true && track.isReceivingData !== false);
    const missingVideoParticipantNames = participants
        .filter(participant => {
        const track = getVideoTrackByParticipant(state, participant);
        return !track || track.muted === true || track.isReceivingData === false;
    })
        .map(participant => getParticipantDisplayName(state, participant.id) || participant.name || participant.id);
    const priorityRoles = Array.from(new Set(policy.priorityParticipantIds
        .map(id => policy.roleByParticipantId[id] || 'guest')));
    // The conference itself says which transport the media uses: only the
    // web app keeps a copy in redux, and it lags a move by a render.
    const conference = state['features/base/conference'].conference;
    let p2pActive = state['features/base/conference'].p2p === true;
    try {
        if (typeof conference?.isP2PActive === 'function') {
            p2pActive = conference.isP2PActive() === true;
        }
    }
    catch { /* keep the redux copy */ }
    const active = getActiveTransport(conference);
    const expectedTransport = expectedMediaTransport({
        hiddenParticipantCount: countConferencePeople(conference)?.hidden ?? 0,
        p2pEnabled: active?.decision.enabled ?? false,
        participantCount: policy.participantCount
    });
    return {
        fallbackBehavior: 'Prioritize interpreter/client video, lower guest thumbnails, preserve captions/audio.',
        // Group-call tiles are capped, not hidden.
        hiddenByPolicyCount: policy.mode === 'group-signing' ? 0 : policy.guestParticipantIds.length,
        expectedTransport,
        jvbExpected: expectedTransport !== 'p2p',
        missingVideoParticipantNames,
        mode: policy.mode,
        p2pActive,
        participantCount: policy.participantCount,
        priorityRoles,
        remoteVideoTrackCount: remoteVideoTracks.length,
        roomKind: policy.roomKind,
        visibleVideoTrackCount: visibleRemoteVideoTracks.length
    };
}
